import { GBPE_NAMESPACE } from '../../../models/user-info'
import { mockEnabled } from '../../../config/script-attributes'

export class PortableAPI {
	public _item: string
	protected _url: string
	private _urlList: Array<string>
	protected _dependentInstance: string
	private _mock: JSON
	protected _mockEnabled: boolean
	private _storageKey: string
	protected _carbonCopyClassName: string
	private _carbonCopyInstanceObsoleteResolverStack
	protected _lookupPromise: Promise<void>
	private _bearerToken: boolean
	protected _fetchPromise

	constructor(apiData) {
		this._item = apiData.item
		this._url = apiData.url
		this._urlList = apiData.urlList
		this._mock = apiData.mock
		this._mockEnabled = apiData.mockEnabled || mockEnabled
		this._bearerToken = apiData.bearerToken
		this._dependentInstance = apiData.dependentInstance
		this._storageKey = GBPE_NAMESPACE + this._item.toUpperCase()
		// Get the naming just right for carbon copy instances
		const clsName = this.capitalize(this._item + 'CarbonCopy')
		this[clsName] = class extends CarbonCopy {}
		this._carbonCopyClassName = clsName
		this._carbonCopyInstanceObsoleteResolverStack = []
	}

	public getInterface() {
		return this.buildCarbonCopy()
	}

	private _fetchPromiseRedirect() {
		return this._lookupPromise?.then(() => this.buildCarbonCopy())
	}

	private capitalize(inputString) {
		return inputString.slice(0, 1).toUpperCase() + inputString.slice(1, inputString.length)
	}

	protected async requestParameters(): Promise<RequestInit> {
		const bearer = this._bearerToken ? 'Bearer ' : ''
		const tokens = await window.gbpe.auth.getValidTokens?.()
		const idToken = tokens?.idToken || window.gbpe.auth.idToken || ''
		const headers: HeadersInit = {
			Authorization: `${bearer + idToken}`,
			locale: window.gbpe.config.locale,
			region: window.gbpe.config.region,
		}
		const param: RequestInit = {
			method: 'GET',
			headers,
		}
		return param
	}

	protected getMockData() {
		return resolve =>
			setTimeout(() => {
				resolve({
					ok: 1,
					status: 200,
					json: () => this._mock,
				})
			}, 200)
	}

	// Multiple fetch API calls from different sources for Missing Info
	// JSOn response consolidated and stored in session storage
	private async _multiFetchApiCall() {
		const promises = []
		const jsonResponse = []
		const requestParams = await this.requestParameters()
		this._lookupPromise = new Promise<void>((resolve, reject) => {
			for (let i = 0; i < this._urlList.length; i++) {
				promises.push(
					(this._mockEnabled ? new Promise(this.getMockData()) : fetch(this._urlList[i], requestParams))
						.then(resp => {
							if (resp && resp['status'] === 200) {
								return resp
							}
							throw this.createError(`Network error - ${this?._urlList[i]}`)
						})
						.catch(() => {
							throw this.createError(`Network error - ${this?._urlList[i]}`)
						})
						.then(async resp => {
							const actionRequiredList = await resp['json']()
							actionRequiredList['actionRequired']?.forEach(urlValue => {
								console.log('Api response' + JSON.stringify(urlValue))
								urlValue.url ? jsonResponse.push(JSON.stringify(urlValue)) : ''
							})
						})
						.catch(error => {
							console.error(error.message)
						})
				)
			}
			Promise.all(promises)
				.then(async () => {
					const actionRequiredVal = jsonResponse.length
						? `{"actionRequired": [${jsonResponse}]}`
						: `{"actionRequired": []}`
					resolve(sessionStorage.setItem(this._storageKey, actionRequiredVal))
					console.log('API response consolidated storage object' + actionRequiredVal)
					document.dispatchEvent(new CustomEvent('gb-sessionStorageRefreshed'))
					this._lookupPromise = null
					console.log('API call for item :' + this._item)
				})
				.catch(error => {
					this.createError(error)
					reject(error)
				})
		})
	}

	private async buildCarbonCopy() {
		await window.gbpe.auth.loaded
		// Error handling
		// Reject promise when user is unauthenticated
		if (window.gbpe.auth.currentAuthState !== 1) {
			return Promise.reject('User is unauthenticated !!!')
		}
		if (this._url === '' || this._url == undefined) {
			return Promise.resolve(`${this._item.toUpperCase()} Disabled for this Navigation: Cancelling API Call...`)
		}
		// 1) Duplicate requests in flight over network
		if (this._lookupPromise) {
			return this._fetchPromiseRedirect()
		}
		// 2) Check for cache - No previous request if cache empty
		const cache = sessionStorage.getItem(this._storageKey)
		if (cache === null) {
			if (this._urlList && this._item?.toLowerCase().includes('missinginfo')) {
				await this._multiFetchApiCall()
			} else {
				const requestParams = await this.requestParameters()
				this._lookupPromise = new Promise<void>((resolve, reject) => {
					this._fetchPromise = (
						this._mockEnabled ? new Promise(this.getMockData()) : fetch(this._url, requestParams)
					)
						//Adding check to for response status is 201 in case of API Response for Korea endpoints via proxy
						.then(resp => {
							if (resp && (resp['status'] === 200 || resp['status'] === 201)) {
								return resp
							}
							throw this.createError(`Network error ${this?._url}`)
						})
						.catch(() => {
							throw this.createError(`Network error ${this?._url}`)
						})
						.then(async resp => {
							resolve(sessionStorage.setItem(this._storageKey, JSON.stringify(await resp['json']())))
							document.dispatchEvent(new CustomEvent('gb-sessionStorageRefreshed'))
							this._lookupPromise = null
							console.log('API call for item :' + this._item)
						})
						.catch(error => {
							this.createError(error)
							reject(error)
						})
				})
			}
			return this._fetchPromiseRedirect()
		}
		// 3) Item is in cache: all paths lead to this exit
		console.log('creating copy of item from cache :' + this._item)
		this._carbonCopyInstanceObsoleteResolverStack = []
		const ccInstance = new this[this._carbonCopyClassName](this, this.safeJsonParse(cache), this._dependentInstance)
		return Promise.resolve(ccInstance)
		// Ensures all requestors use promise syntax - however - some may be chained
	}

	private trackPendingCarbonCopyInstanceObsoleteResolver(resolver) {
		this._carbonCopyInstanceObsoleteResolverStack.push(resolver)
	}

	//TODO: may need to overload this function in subclass so that an additional API call is made to reset server
	//info in login-refresh api endpoint
	public reset() {
		const stack = this._carbonCopyInstanceObsoleteResolverStack
		// Purge storage
		sessionStorage.removeItem(this._storageKey)
		// Triggers obsoletePromise on all other instances created to date; then remove the references
		stack.map(resolver => resolver())
		stack.splice(0, stack.length)
	}

	protected createError(msg = 'Global Brands Portable Experiences', name = 'GBPE' + '-Error') {
		this._lookupPromise = null
		const e = new Error(msg)
		e.name = name
		return e
	}

	/**
	 * Function to retry a call to an API.  Returns true if the call succeeds and false otherwise.
	 * @param callpath - The path to the API call being made.
	 * @param tries - The number of attempts to retry the call.
	 * @param waitTime - The time in milliseconds to wait between each try.
	 * @param error - An error to be thrown if an error is caught from the call.
	 * @param failedMessage - (Optional) A message that is output to the console upon call failure.
	 */
	protected async retryAPICall(
		callPath: string,
		tries: number,
		waitTime: number,
		error: Error,
		failedMessage?: string
	): Promise<boolean> {
		let callLoopCount = 0
		let responseStatus
		const requestParams = await this.requestParameters()

		while (callLoopCount < tries) {
			try {
				const response = fetch(callPath, requestParams)
					.then(response => response.json())
					.catch(err => {
						throw err
					})

				responseStatus = response['status']
				callLoopCount++
			} catch {
				throw error
			} finally {
				if (responseStatus === 200) {
					//Call Succeeded
					// eslint-disable-next-line no-unsafe-finally
					return true
				} else {
					if (failedMessage) {
						console.log(failedMessage + ' (' + responseStatus + ')')
					}

					//Break instead of waiting after the final try.
					if (callLoopCount === tries) {
						// eslint-disable-next-line no-unsafe-finally
						break
					}

					//Wait and then retry the call
					await new Promise(resolve => setTimeout(resolve, waitTime))
				}
			}
		}
		//Call Failed
		return false
	}

	private safeJsonParse(inputString, errResp = {}) {
		// JSON parse can fail if undefined is encoded - or some other invalid value
		try {
			return JSON.parse(inputString)
		} catch (e) {
			console.error(this.createError('JSON Parse Error - Unexpected Input'))
			return errResp
		}
	}
}

export class CarbonCopy {
	private reset
	private assignData
	private whenObsolete
	// CarbonCopy is the base class for profile, rewards, vehicles, missing, and copies data over from cache or response to each instance
	constructor(portableAPI, data, dependency) {
		this.reset = () => portableAPI.reset()
		this.assignData = data => {
			this['data'] = data
			delete this.assignData
		}
		this.assignData(data)
		// add dependent objects
		dependency ? Object.assign(this, dependency) : ''
		// whenObsolete: Use this promise to get notified when reset is called on thi s API type
		this.whenObsolete = new Promise(resolver => {
			portableAPI.trackPendingCarbonCopyInstanceObsoleteResolver(resolver)
		}).then(() => {
			console.log('Reset called for item:' + portableAPI._item)
			document.dispatchEvent(new CustomEvent('gb-myaccountReset', { detail: { itemName: portableAPI._item } }))
		})
	}
}
